import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { Plus, Trash2, Settings, AlertTriangle, Copy, Check, ChevronDown, Info } from 'lucide-react';

function Tooltip({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <Info size={13} className="text-gray-400 cursor-help" />
      {visible && (
        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-gray-800 text-white text-xs rounded px-2 py-1.5 z-[10001] leading-relaxed shadow-lg whitespace-normal">
          {text}
        </span>
      )}
    </span>
  );
}

interface ApiKey {
  id: string;
  name: string;
  key?: string;
  isActive: boolean;
  usageCount: number;
  creditsUsed: number;
  lastUsedAt?: string;
  createdAt: string;
  modelSelectionMode?: 'all' | 'custom_only' | 'global_only' | 'privacy_only';
  modelSelectionPriority?: 'rank' | 'price';
  modelListId?: string;
  activeTagSlugs?: string[];
  taskDeterminationEnabled?: boolean;
  spendWindows?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    spendLimit: number | null;
    currentSpend: number;
    periodExpiresAt: string;
  }[];
}

interface ModelList {
  id: string;
  name: string;
  description?: string;
  items?: { modelId: string }[];
}

interface Category {
  slug: string;
  name: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [modelLists, setModelLists] = useState<ModelList[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [pendingConfigKeyId, setPendingConfigKeyId] = useState<string | null>(null);
  const [showKeySettingsModal, setShowKeySettingsModal] = useState(false);
  const [keyModelSelectionMode, setKeyModelSelectionMode] = useState<'all' | 'custom_only' | 'global_only' | 'privacy_only'>('all');
  const [keyModelSelectionPriority, setKeyModelSelectionPriority] = useState<'rank' | 'price'>('rank');
  const [keyModelListId, setKeyModelListId] = useState<string>('');
  const [keyActiveTagSlugs, setKeyActiveTagSlugs] = useState<string[]>([]);
  const [keyTaskDeterminationEnabled, setKeyTaskDeterminationEnabled] = useState<boolean>(true);
  const [keyDailySpendLimit, setKeyDailySpendLimit] = useState<string>('');
  const [keyWeeklySpendLimit, setKeyWeeklySpendLimit] = useState<string>('');
  const [keyMonthlySpendLimit, setKeyMonthlySpendLimit] = useState<string>('');
  const [accountDailyLimit, setAccountDailyLimit] = useState<string>('');
  const [accountWeeklyLimit, setAccountWeeklyLimit] = useState<string>('');
  const [accountMonthlyLimit, setAccountMonthlyLimit] = useState<string>('');
  const [accountTdOverage, setAccountTdOverage] = useState<boolean>(false);
  const [accountSpendWindows, setAccountSpendWindows] = useState<{ frequency: string; currentSpend: number; periodExpiresAt: string }[]>([]);
  const [tagModelCache, setTagModelCache] = useState<Record<string, string[]>>({});
  const [accountLimitsSaving, setAccountLimitsSaving] = useState(false);
  const [accountLimitsSaved, setAccountLimitsSaved] = useState(false);
  const [accountLimitsOpen, setAccountLimitsOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [tagDropdownRect, setTagDropdownRect] = useState<DOMRect | null>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const tagTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    loadApiKeys();
    loadModelLists();
    loadCategories();
    loadAccountLimits();
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const uncached = keyActiveTagSlugs.filter(slug => !(slug in tagModelCache));
    if (uncached.length === 0) return;
    Promise.all(
      uncached.map(slug =>
        apiService.getTagModels(slug)
          .then(res => ({ slug, modelIds: res.modelIds }))
          .catch(() => ({ slug, modelIds: [] as string[] }))
      )
    ).then(results => {
      setTagModelCache(prev => {
        const next = { ...prev };
        results.forEach(r => { next[r.slug] = r.modelIds; });
        return next;
      });
    });
  }, [keyActiveTagSlugs]);

  const intersectionWarning = useMemo(() => {
    if (!keyModelListId || keyActiveTagSlugs.length === 0) return null;
    const list = modelLists.find(l => l.id === keyModelListId);
    if (!list?.items?.length) return null;
    const listModelIds = new Set(list.items.map((i: any) => i.modelId));
    const emptyTags: string[] = [];
    for (const slug of keyActiveTagSlugs) {
      if (!(slug in tagModelCache)) continue;
      const tagModels = tagModelCache[slug];
      if (tagModels.length === 0) continue;
      const hasOverlap = tagModels.some(id => listModelIds.has(id));
      if (!hasOverlap) {
        const cat = categories.find(c => c.slug === slug);
        emptyTags.push(cat?.name || slug);
      }
    }
    if (emptyTags.length === 0) return null;
    return `No models overlap between "${list.name}" and the ${emptyTags.map(t => `"${t}"`).join(', ')} tag${emptyTags.length > 1 ? 's' : ''}. These tag filters will be silently skipped at runtime.`;
  }, [keyModelListId, keyActiveTagSlugs, tagModelCache, modelLists, categories]);

  const loadModelLists = async () => {
    try {
      const res = await apiService.getModelLists();
      setModelLists((res as any).data || res);
    } catch {
      // non-fatal
    }
  };

  const loadCategories = async () => {
    try {
      const data = await apiService.listCategories();
      setCategories((data.categories || []).filter((c: Category) => c.slug !== 'privacy'));
    } catch {
      // non-fatal
    }
  };

  const loadAccountLimits = async () => {
    try {
      const data = await apiService.getAccountLimits();
      setAccountDailyLimit(data.dailySpendLimit != null ? String(data.dailySpendLimit) : '');
      setAccountWeeklyLimit(data.weeklySpendLimit != null ? String(data.weeklySpendLimit) : '');
      setAccountMonthlyLimit(data.monthlySpendLimit != null ? String(data.monthlySpendLimit) : '');
      setAccountSpendWindows(data.spendWindows || []);
      setAccountTdOverage(data.tdOverageEnabled ?? false);
    } catch {
      // non-fatal — server might not have the endpoint yet
    }
  };

  const handleSaveAccountLimits = async () => {
    setAccountLimitsSaving(true);
    try {
      await apiService.updateAccountLimits({
        dailySpendLimit:   accountDailyLimit   !== '' ? parseFloat(accountDailyLimit)   : null,
        weeklySpendLimit:  accountWeeklyLimit  !== '' ? parseFloat(accountWeeklyLimit)  : null,
        monthlySpendLimit: accountMonthlyLimit !== '' ? parseFloat(accountMonthlyLimit) : null,
        tdOverageEnabled:  accountTdOverage,
      });
      setAccountLimitsSaved(true);
      setTimeout(() => setAccountLimitsSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save account limits');
    } finally {
      setAccountLimitsSaving(false);
    }
  };

  const handleEditKeySettings = (key: ApiKey) => {
    setEditingKey(key);
    setKeyModelSelectionMode(key.modelSelectionMode || 'all');
    setKeyModelSelectionPriority(key.modelSelectionPriority || 'rank');
    setKeyModelListId(key.modelListId || '');
    setKeyActiveTagSlugs(key.activeTagSlugs || []);
    setKeyTaskDeterminationEnabled(key.taskDeterminationEnabled ?? true);
    const windows = key.spendWindows || [];
    const w = (freq: string) => windows.find(x => x.frequency === freq);
    setKeyDailySpendLimit(w('daily')?.spendLimit != null ? String(w('daily')!.spendLimit) : '');
    setKeyWeeklySpendLimit(w('weekly')?.spendLimit != null ? String(w('weekly')!.spendLimit) : '');
    setKeyMonthlySpendLimit(w('monthly')?.spendLimit != null ? String(w('monthly')!.spendLimit) : '');
    setShowKeySettingsModal(true);
  };

  const handleSaveKeySettings = async () => {
    if (!editingKey) return;

    try {
      await apiService.updateApiKey(editingKey.id, {
        modelSelectionMode: keyModelSelectionMode,
        modelSelectionPriority: keyModelSelectionPriority,
        modelListId: keyModelListId || null,
        activeTagSlugs: keyActiveTagSlugs,
        taskDeterminationEnabled: keyTaskDeterminationEnabled,
      });
      // Save spend limits per-frequency (only if changed)
      await Promise.all(([
        ['daily',   keyDailySpendLimit],
        ['weekly',  keyWeeklySpendLimit],
        ['monthly', keyMonthlySpendLimit],
      ] as const).map(([freq, val]) =>
        apiService.setKeySpendWindow(editingKey.id, freq, val !== '' ? parseFloat(val) : null)
      ));
      setShowKeySettingsModal(false);
      setEditingKey(null);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message || 'Failed to save API key settings');
    }
  };

  const loadApiKeys = async () => {
    try {
      const keys = await apiService.getApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const result = await apiService.createApiKey(newKeyName.trim());
      setCreatedKey(result.key);
      setPendingConfigKeyId(result.id);
      setNewKeyName('');
      setShowCreateModal(false);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const maskKey = (keyId: string) => {
    // Always show the key ID with masking - full keys are never returned after creation
    return `fxk_${keyId.substring(0, 8)}••••••••••••••••••••••••••••••••••••••••••••••••••••`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const copyCreatedKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const dismissCreatedKey = () => {
    setCreatedKey(null);
    setKeyCopied(false);
    if (pendingConfigKeyId) {
      const key = apiKeys.find(k => k.id === pendingConfigKeyId);
      setPendingConfigKeyId(null);
      if (key) handleEditKeySettings(key);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your API keys for accessing AskArc API
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create API Key
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Account-level spend limits */}
      <div className="bg-white shadow sm:rounded-lg">
        <button
          onClick={() => setAccountLimitsOpen(o => !o)}
          className="w-full flex items-center justify-between px-6 py-4 text-left"
        >
          <div>
            <h2 className="text-base font-semibold text-gray-900">Account Spend Limits</h2>
            <p className="text-sm text-gray-500 mt-0.5">Global caps applied across all API keys</p>
          </div>
          <ChevronDown size={18} className={`text-gray-400 transition-transform flex-shrink-0 ${accountLimitsOpen ? 'rotate-180' : ''}`} />
        </button>

        {accountLimitsOpen && (
          <div className="px-6 pb-6 border-t border-gray-100">
            <div className="mt-4 grid grid-cols-3 gap-4">
              {(['daily', 'weekly', 'monthly'] as const).map(period => {
                const val = period === 'daily' ? accountDailyLimit : period === 'weekly' ? accountWeeklyLimit : accountMonthlyLimit;
                const setter = period === 'daily' ? setAccountDailyLimit : period === 'weekly' ? setAccountWeeklyLimit : setAccountMonthlyLimit;
                const limitNum = parseFloat(val);
                const window = accountSpendWindows.find(w => w.frequency === period);
                const currentSpend = window?.currentSpend ?? 0;
                const limitExceeded = !isNaN(limitNum) && currentSpend >= limitNum;
                return (
                  <div key={period}>
                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">{period} Limit ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Unlimited"
                      value={val}
                      onChange={e => setter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                    {window && (
                      <p className="mt-1 text-xs text-gray-500">
                        <span className={limitExceeded ? 'text-red-600 font-medium' : 'text-gray-700'}>${currentSpend.toFixed(4)}</span>
                        {!isNaN(limitNum) ? ` / $${limitNum.toFixed(2)} this ${period}` : ' spent this ' + period}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-4 flex items-center gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accountTdOverage}
                  onChange={e => setAccountTdOverage(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Allow Task Determination overage billing</span>
              </label>
              <Tooltip text="When your free TD quota is exhausted: OFF = requests are blocked, ON = TD calls continue at the overage rate." />
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSaveAccountLimits}
                disabled={accountLimitsSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm disabled:opacity-50"
              >
                {accountLimitsSaving ? 'Saving…' : 'Save Account Limits'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Created Key Modal */}
      {createdKey && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] !m-0" onClick={() => setCreatedKey(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Warning banner */}
            <div className="bg-amber-400 px-5 py-4 flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-amber-900 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-base">Save your API key now</p>
                <p className="text-amber-800 text-sm mt-0.5">
                  This is the <strong>only time</strong> your full key will be shown. It cannot be recovered after you close this dialog.
                </p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-3">Your new API key:</p>

              {/* Key display */}
              <div className="bg-gray-900 rounded-lg p-4 flex items-center justify-between gap-3 border-2 border-amber-300">
                <code className="text-green-400 text-sm font-mono break-all flex-1 leading-relaxed">
                  {createdKey}
                </code>
              </div>

              {/* Actions */}
              <div className="mt-5 flex gap-3">
                <button
                  onClick={copyCreatedKey}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                    keyCopied
                      ? 'bg-green-600 text-white'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                >
                  {keyCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copy API Key
                    </>
                  )}
                </button>
                <button
                  onClick={dismissCreatedKey}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold text-sm hover:bg-gray-200 transition-all"
                >
                  I've saved it — configure key →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-96 p-5" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New API Key
              </h3>
              <input
                type="text"
                placeholder="Enter key name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={!newKeyName.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Key Settings Modal */}
      {showKeySettingsModal && editingKey && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0 p-4" onClick={() => { setShowKeySettingsModal(false); setEditingKey(null); setTagDropdownOpen(false); setTagSearch(''); }}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl flex flex-col" style={{ maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b flex-shrink-0">
              <h3 className="text-lg font-medium text-gray-900">
                API Key Settings: {editingKey.name}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Configure how this API key interacts with models
              </p>
            </div>

            {/* Body — 2 columns */}
            <div className="px-6 py-5 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">

                {/* Left column — selection behaviour */}
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Model Selection Mode
                      <Tooltip text="Controls which models this key can access. 'All' uses global + custom. 'Privacy-Compliant' fails if no privacy models are configured." />
                    </label>
                    <select
                      value={keyModelSelectionMode}
                      onChange={(e) => setKeyModelSelectionMode(e.target.value as 'all' | 'custom_only' | 'global_only' | 'privacy_only')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="all">All Models (Global + Custom)</option>
                      <option value="custom_only">My Custom Models Only</option>
                      <option value="global_only">Global Models Only</option>
                      <option value="privacy_only">Privacy-Compliant Only</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Model Selection Priority
                      <Tooltip text="Rank First picks the highest-quality model. Price First picks the most cost-effective model in the eligible pool." />
                    </label>
                    <select
                      value={keyModelSelectionPriority}
                      onChange={(e) => setKeyModelSelectionPriority(e.target.value as 'rank' | 'price')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="rank">Rank First (Quality)</option>
                      <option value="price">Price First (Cost)</option>
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      Task Determination
                      <Tooltip text="When forced, the system always picks the best model and ignores any model passed in the request. When off, the caller controls model selection — TD still runs if no model is specified." />
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={keyTaskDeterminationEnabled}
                        onChange={(e) => setKeyTaskDeterminationEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Force Task Determination</span>
                    </label>
                  </div>
                </div>

                {/* Right column — restrictions */}
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="flex items-center text-sm font-medium text-gray-700">
                        Model List <span className="text-gray-400 font-normal ml-1">(optional)</span>
                        <Tooltip text="Restricts this key to only models in a curated list. If the list is empty the request will fail." />
                      </label>
                      <Link to="/models" className="text-xs text-indigo-600 hover:underline">Manage →</Link>
                    </div>
                    <select
                      value={keyModelListId}
                      onChange={(e) => setKeyModelListId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    >
                      <option value="">No list — all available models</option>
                      {modelLists.map(list => (
                        <option key={list.id} value={list.id}>
                          {list.name}{list.items?.length !== undefined ? ` (${list.items.length} model${list.items.length !== 1 ? 's' : ''})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {categories.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="flex items-center text-sm font-medium text-gray-700">
                          Tag-Based Filtering <span className="text-gray-400 font-normal ml-1">(optional)</span>
                          <Tooltip text="When a tag fires on a request to this key, only models with that tag enabled will be used." />
                        </label>
                        <Link to="/category-rules" className="text-xs text-indigo-600 hover:underline">Manage →</Link>
                      </div>
                      <div className="relative" ref={tagDropdownRef}>
                        <button
                          ref={tagTriggerRef}
                          type="button"
                          onClick={() => {
                            if (tagTriggerRef.current) setTagDropdownRect(tagTriggerRef.current.getBoundingClientRect());
                            setTagDropdownOpen(o => !o);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <span className={keyActiveTagSlugs.length === 0 ? 'text-gray-400' : 'text-gray-800'}>
                            {keyActiveTagSlugs.length === 0
                              ? 'No tags selected'
                              : `${keyActiveTagSlugs.length} tag${keyActiveTagSlugs.length !== 1 ? 's' : ''} selected`}
                          </span>
                          <ChevronDown size={14} className={`text-gray-400 transition-transform ${tagDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {tagDropdownOpen && tagDropdownRect && (
                          <div
                            ref={tagDropdownRef}
                            className="fixed z-[10000] bg-white border border-gray-200 rounded-md shadow-lg"
                            style={{
                              top: tagDropdownRect.bottom + 4,
                              left: tagDropdownRect.left,
                              width: tagDropdownRect.width,
                            }}
                          >
                            <div className="px-2 py-2 border-b border-gray-100">
                              <input
                                type="text"
                                value={tagSearch}
                                onChange={e => setTagSearch(e.target.value)}
                                placeholder="Search tags…"
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                autoFocus
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-40 overflow-y-auto">
                              {categories.filter(cat => cat.name.toLowerCase().includes(tagSearch.toLowerCase())).map(cat => {
                                const checked = keyActiveTagSlugs.includes(cat.slug);
                                return (
                                  <label key={cat.slug} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setKeyActiveTagSlugs(prev => [...prev, cat.slug]);
                                        } else {
                                          setKeyActiveTagSlugs(prev => prev.filter(s => s !== cat.slug));
                                        }
                                      }}
                                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">{cat.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {intersectionWarning && (
                    <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">{intersectionWarning}</p>
                    </div>
                  )}
                </div>

              </div>

              {/* Spend Limits — full-width section below the two columns */}
              <div className="mt-5 pt-4 border-t border-gray-100">
                <h4 className="flex items-center text-sm font-semibold text-gray-800 mb-3">
                  Spend Limits
                  <Tooltip text="Leave blank for unlimited. All three can be set independently. Resets: daily at midnight UTC, weekly on Monday, monthly on the 1st." />
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  {([
                    { label: 'Daily ($)', freq: 'daily'   as const, val: keyDailySpendLimit,   setter: setKeyDailySpendLimit },
                    { label: 'Weekly ($)', freq: 'weekly'  as const, val: keyWeeklySpendLimit,  setter: setKeyWeeklySpendLimit },
                    { label: 'Monthly ($)', freq: 'monthly' as const, val: keyMonthlySpendLimit, setter: setKeyMonthlySpendLimit },
                  ]).map(({ label, freq, val, setter }) => {
                    const win = editingKey?.spendWindows?.find(w => w.frequency === freq);
                    return (
                      <div key={freq}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Unlimited"
                          value={val}
                          onChange={e => setter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                        {win && (
                          <p className="mt-1 text-xs text-gray-500">
                            Spent: <span className="font-medium text-gray-700">${win.currentSpend.toFixed(4)}</span>
                            {val !== '' && ` / $${parseFloat(val).toFixed(2)}`}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end space-x-3">
              <button
                onClick={() => { setShowKeySettingsModal(false); setEditingKey(null); setTagDropdownOpen(false); setTagSearch(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveKeySettings}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {apiKeys.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No API keys yet. Create your first one to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {apiKeys.map((key) => (
              <li key={key.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{key.name}</h3>

                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Usage: {key.usageCount} requests</span>
                      <span>Cost: ${key.creditsUsed.toFixed(4)}</span>
                      <span>
                        Last used: {key.lastUsedAt
                          ? new Date(key.lastUsedAt).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                      <span>
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {(key.spendWindows || []).filter(w => w.spendLimit != null).length > 0 && (
                      <div className="mt-1.5 flex items-center gap-3">
                        {(key.spendWindows || []).filter(w => w.spendLimit != null).map(w => (
                          <span key={w.frequency} className="text-xs text-gray-500">
                            <span className="capitalize">{w.frequency}</span>:{' '}
                            <span className={`font-medium ${w.currentSpend >= w.spendLimit! ? 'text-red-600' : 'text-gray-700'}`}>
                              ${w.currentSpend.toFixed(4)}
                            </span>
                            {' / '}${w.spendLimit!.toFixed(2)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ml-4 flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      key.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {key.isActive ? 'Active' : 'Revoked'}
                    </span>
                    {key.isActive && (
                      <>
                        <button
                          onClick={() => handleEditKeySettings(key)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Configure settings for this API key"
                        >
                          <Settings size={20} />
                        </button>
                        <button
                          onClick={() => handleRevokeKey(key.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Revoke this API key"
                        >
                          <Trash2 size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>

      {accountLimitsSaved && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg text-sm font-medium z-50">
          Account limits saved successfully
        </div>
      )}
    </>
  );
}
